/**
 * lib/cards/defaults/mountains.js — Premium template default bottom art.
 *
 * Three-layer mountain silhouette range + optional bird silhouettes.
 * Drawn mathematically so it tints from the tenant's brand colour
 * like the Minimal wave does.
 *
 * Design reference: Jez's Apr 23 screenshot of the Coastal "Sebastian
 * Roberts" card — layered blue peaks with lighter distant ranges,
 * two small birds flying in the sky area.
 *
 * Rendered using jsPDF's lines() primitive (straight-path polygon)
 * rather than bezier curves — simpler, reliable, and produces crisp
 * silhouettes at print resolution.
 */

import { CR80, premultiplyAlpha } from '../shared.js';

/**
 * Draw one mountain layer as a polygon with N sharp peaks.
 *
 * @param pdf          jsPDF
 * @param brandColor   { r, g, b }
 * @param alpha        0.0 - 1.0 (layering via premultiplied alpha)
 * @param baselineY    the Y where peaks start (mountain tops)
 * @param maxHeight    how tall the tallest peak reaches (mm)
 * @param peaks        array of peak heights 0-1 (e.g. [0.3, 0.9, 0.5, 0.7, 0.4])
 */
function drawMountainLayer(pdf, brandColor, alpha, baselineY, maxHeight, peaks) {
  const { r, g, b } = premultiplyAlpha(brandColor, alpha);
  pdf.setFillColor(r, g, b);

  // We'll build the polygon via a sequence of line segments between
  // (x, y) points, then close the bottom.
  const W = CR80.widthMm;
  const H = CR80.heightMm;
  const peakSpacing = W / (peaks.length - 1);

  // Build absolute points: top of the polygon zigzagging across
  const points = [];
  for (let i = 0; i < peaks.length; i++) {
    const x = i * peakSpacing;
    const y = baselineY - peaks[i] * maxHeight;
    points.push({ x, y });
  }

  // jsPDF's lines([...], startX, startY) takes RELATIVE deltas from
  // the start point, not absolute. Convert.
  const deltas = [];
  for (let i = 1; i < points.length; i++) {
    deltas.push([
      points[i].x - points[i - 1].x,
      points[i].y - points[i - 1].y,
    ]);
  }
  // Close polygon: down to bottom-right corner, across to bottom-left,
  // back up to start
  deltas.push([0, H - points[points.length - 1].y]);
  deltas.push([-W, 0]);
  deltas.push([0, points[0].y - H]);

  pdf.lines(deltas, points[0].x, points[0].y, [1, 1], 'F');
}

/**
 * Draw a small bird silhouette (stylised M-shape) at a given position.
 *
 * @param pdf          jsPDF
 * @param x            centre X in mm
 * @param y            centre Y in mm
 * @param size         total wingspan in mm (e.g. 2.5)
 * @param color        { r, g, b }
 */
function drawBird(pdf, x, y, size, color) {
  pdf.setDrawColor(color.r, color.g, color.b);
  pdf.setLineWidth(0.3);
  const half = size / 2;
  const lift = size * 0.3;

  // Left wing: curve down then up
  pdf.line(x - half, y, x - half / 2, y - lift);
  pdf.line(x - half / 2, y - lift, x, y);
  // Right wing
  pdf.line(x, y, x + half / 2, y - lift);
  pdf.line(x + half / 2, y - lift, x + half, y);
}

/**
 * Paint the full Premium-template mountain band.
 *
 * @param pdf          jsPDF instance
 * @param brandColor   { r, g, b } — tints all three mountain layers
 * @param options      { topY, showBirds }
 *                       topY     — where the farthest peaks start (mm)
 *                                  default 38mm
 *                       showBirds — render 2 stylised birds in the sky
 *                                   default true
 */
export function drawMountainRange(pdf, brandColor, options = {}) {
  const topY = options.topY !== undefined ? options.topY : 38;
  const showBirds = options.showBirds !== false;
  const H = CR80.heightMm;

  // Layer 1 (farthest, lightest): low, smooth peaks
  drawMountainLayer(
    pdf,
    brandColor,
    0.25,
    topY + 4,
    5,
    [0.2, 0.5, 0.3, 0.7, 0.4, 0.55, 0.25, 0.6, 0.35, 0.5, 0.3],
  );

  // Layer 2 (middle): taller, sharper
  drawMountainLayer(
    pdf,
    brandColor,
    0.45,
    topY + 6,
    6.5,
    [0.3, 0.8, 0.5, 1.0, 0.6, 0.9, 0.4, 0.75, 0.55, 0.85, 0.5, 0.7],
  );

  // Layer 3 (nearest, darkest): boldest peaks, closest to footer
  drawMountainLayer(
    pdf,
    brandColor,
    0.65,
    topY + 8,
    7,
    [0.5, 1.0, 0.6, 0.85, 1.0, 0.7, 0.95, 0.55, 0.9, 0.75, 0.85, 0.6, 0.8],
  );

  // Birds in sky (between logo block and mountain tops)
  if (showBirds) {
    // Draw at middle alpha so they read without being harsh
    const birdColor = premultiplyAlpha(brandColor, 0.55);
    drawBird(pdf, CR80.widthMm - 18, topY - 4, 2.5, birdColor);
    drawBird(pdf, CR80.widthMm - 12, topY - 6, 2.0, birdColor);
  }

  // Solid brand-coloured footer strip
  const footerY = H - 4;
  pdf.setFillColor(brandColor.r, brandColor.g, brandColor.b);
  pdf.rect(0, footerY, CR80.widthMm, H - footerY, 'F');
}
